import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Consent } from '../../../../generated/graphql'
import { useForm } from 'react-hook-form'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { usePrevious } from 'react-use'
import { useEffect } from 'react'
import { SwitchInput } from '../../../../inputs/switch-input'

type Form = Consent & { id: string }

type Props = {
  open: boolean
  onClose: () => void
  onSave: (formData: Form) => void
  data: Consent | null
}

export const ConsentDialog = (props: Props) => {
  const { open, data, onClose } = props
  const { t } = useTranslation(['apps', 'shared'])
  const prevOpen = usePrevious(open)

  const { control, reset, handleSubmit } = useForm<Form>({
    defaultValues: {
      consent_id: '',
      name: '',
      description: '',
      required: false,
    },
  })

  useEffect(() => {
    if (open !== prevOpen) {
      reset(data || {}, { keepDefaultValues: true })
    }
  }, [data, open, prevOpen, reset])

  const onSubmit = (formData: Form) => {
    props.onSave(formData)
    props.onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ mb: 2 }}>
        {t('apps:recognition.consent_dialog_label')}
      </DialogTitle>

      <DialogContent>
        <form
          id="consent-form"
          noValidate
          onSubmit={(e) => {
            e.stopPropagation()
            e.preventDefault()

            return handleSubmit(onSubmit)()
          }}
        >
          <Stack gap={2}>
            <TextFieldInput
              name="consent_id"
              control={control}
              label={t('apps:recognition.consent_id_label')}
              required
            />

            <TextFieldInput
              name="name"
              control={control}
              label={t('apps:recognition.consent_name_label')}
              required
            />

            <TextFieldInput
              name="description"
              control={control}
              label={t('apps:recognition.consent_description_label')}
              required
            />

            <ListItem
              component="div"
              sx={{ '&.MuiListItem-root': { p: 0 } }}
              secondaryAction={
                <SwitchInput name="required" control={control} />
              }
            >
              <ListItemText>
                {t('apps:recognition.consent_required_label')}
              </ListItemText>
            </ListItem>
          </Stack>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t('shared:action.close')}
        </Button>
        <Button type="submit" color="primary" form="consent-form">
          {t('shared:action.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
